import React from 'react';
import {
  type AdminAICredits,
  type AICreditsTargetType,
  getAdminAICredits,
  updateAdminAICredits
} from '~/common/api-client';

interface State {
  data: AdminAICredits | null;
  error: Error | null;
  loading: boolean;
  load: (targetType: AICreditsTargetType, targetId: string) => Promise<void>;
  save: (
    targetType: AICreditsTargetType,
    targetId: string,
    usedCredits: number
  ) => Promise<AdminAICredits>;
}

const useAdminAICredits = (token: string): State => {
  const [data, setData] = React.useState<AdminAICredits | null>(null);
  const [error, setError] = React.useState<Error | null>(null);
  const [loading, setLoading] = React.useState(false);

  const runRequest = React.useCallback(
    async (request: () => Promise<AdminAICredits>) => {
      setLoading(true);
      setError(null);
      try {
        const result = await request();
        setData(result);
        return result;
      } catch (requestError) {
        setError(requestError as Error);
        throw requestError;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const load = React.useCallback(
    async (targetType: AICreditsTargetType, targetId: string) => {
      await runRequest(() =>
        getAdminAICredits({ token, targetType, targetId })
      );
    },
    [runRequest, token]
  );

  const save = React.useCallback(
    (targetType: AICreditsTargetType, targetId: string, usedCredits: number) =>
      runRequest(() =>
        updateAdminAICredits({
          token,
          targetType,
          targetId,
          usedCredits
        })
      ),
    [runRequest, token]
  );

  return { data, error, loading, load, save };
};

export default useAdminAICredits;
